// src/types/separation.ts

export type UserRole = 'employee' | 'supervisor' | 'manager' | 'hr' | 'admin' | 'superadmin' | 'hr head' | 'hr admin' | 'director' | 'founder';

export interface RolePermissions {
  canSubmitResignation: boolean;
  canWithdrawResignation: boolean;
  canApproveManager: boolean;
  canApproveHR: boolean;
  canApproveAdmin: boolean;
  canTerminateEmployee: boolean;
  canManageChecklist: boolean;
  canCreateSettlement: boolean;
  canApproveSettlement: boolean;
  canViewOwnSeparation: boolean;
  canViewAllSeparation: boolean;
}

export const getRolePermissions = (role: UserRole): RolePermissions => {
  const normalizedRole = role?.toLowerCase() || 'employee';
  
  const permissions: Record<string, RolePermissions> = {
    employee: {
      canSubmitResignation: true,
      canWithdrawResignation: true,
      canApproveManager: false,
      canApproveHR: false,
      canApproveAdmin: false,
      canTerminateEmployee: false,
      canManageChecklist: false,
      canCreateSettlement: false,
      canApproveSettlement: false,
      canViewOwnSeparation: true,
      canViewAllSeparation: false,
    },
    manager: {
      canSubmitResignation: true,
      canWithdrawResignation: false,
      canApproveManager: true,
      canApproveHR: false,
      canApproveAdmin: false,
      canTerminateEmployee: false,
      canManageChecklist: false,
      canCreateSettlement: false,
      canApproveSettlement: false,
      canViewOwnSeparation: true,
      canViewAllSeparation: false,
    },
    hr: {
      canSubmitResignation: true,
      canWithdrawResignation: false,
      canApproveManager: false,
      canApproveHR: true,
      canApproveAdmin: false,
      canTerminateEmployee: true,
      canManageChecklist: true,
      canCreateSettlement: true,
      canApproveSettlement: true,
      canViewOwnSeparation: true,
      canViewAllSeparation: true,
    },
    'hr head': {
      canSubmitResignation: true,
      canWithdrawResignation: false,
      canApproveManager: false,
      canApproveHR: true,
      canApproveAdmin: false,
      canTerminateEmployee: true,
      canManageChecklist: true,
      canCreateSettlement: true,
      canApproveSettlement: true,
      canViewOwnSeparation: true,
      canViewAllSeparation: true,
    },
    'hr admin': {
      canSubmitResignation: true,
      canWithdrawResignation: false,
      canApproveManager: false,
      canApproveHR: true,
      canApproveAdmin: false,
      canTerminateEmployee: true,
      canManageChecklist: true,
      canCreateSettlement: true,
      canApproveSettlement: true,
      canViewOwnSeparation: true,
      canViewAllSeparation: true,
    },
    admin: {
      canSubmitResignation: true,
      canWithdrawResignation: false,
      canApproveManager: false,
      canApproveHR: false,
      canApproveAdmin: true,
      canTerminateEmployee: true,
      canManageChecklist: true,
      canCreateSettlement: true,
      canApproveSettlement: true,
      canViewOwnSeparation: true,
      canViewAllSeparation: true,
    },
    superadmin: {
      canSubmitResignation: true,
      canWithdrawResignation: false,
      canApproveManager: false,
      canApproveHR: false,
      canApproveAdmin: true,
      canTerminateEmployee: true,
      canManageChecklist: true,
      canCreateSettlement: true,
      canApproveSettlement: true,
      canViewOwnSeparation: true,
      canViewAllSeparation: true,
    },
    director: {
      canSubmitResignation: true,
      canWithdrawResignation: false,
      canApproveManager: false,
      canApproveHR: false,
      canApproveAdmin: true,
      canTerminateEmployee: true,
      canManageChecklist: true,
      canCreateSettlement: true,
      canApproveSettlement: true,
      canViewOwnSeparation: true,
      canViewAllSeparation: true,
    },
    founder: {
      canSubmitResignation: true,
      canWithdrawResignation: false,
      canApproveManager: false,
      canApproveHR: false,
      canApproveAdmin: true,
      canTerminateEmployee: true,
      canManageChecklist: true,
      canCreateSettlement: true,
      canApproveSettlement: true,
      canViewOwnSeparation: true,
      canViewAllSeparation: true,
    },
  };
  
  return permissions[normalizedRole] || permissions.employee;
};

// Employee identity fields the backend now attaches to list rows so the UI can
// show a real name/code instead of a raw UUID. Optional for backward-compat.
export interface EmployeeBrief {
  employee_name?: string;
  employee_code?: string | null;
  designation?: string | null;
}

export interface ResignationRequest extends EmployeeBrief {
  id: string;
  employee_id: string;
  user_id: string;
  resignation_date: string | null;
  requested_last_day: string | null;
  actual_last_day: string | null;
  notice_period_days: number;
  reason: string;
  additional_notes: string | null;
  status: string;
  manager_id: string | null;
  manager_remarks: string | null;
  manager_actioned_at: string | null;
  hr_user_id: string | null;
  hr_remarks: string | null;
  hr_actioned_at: string | null;
  admin_user_id: string | null;
  admin_remarks: string | null;
  admin_actioned_at: string | null;
  early_release_requested: boolean;
  early_release_reason: string | null;
  early_release_approved: boolean | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface TerminationRecord extends EmployeeBrief {
  id: string;
  employee_id: string;
  user_id: string;
  terminated_by: string;
  termination_date: string | null;
  last_working_day: string | null;
  termination_type: string;
  reason: string;
  additional_notes: string | null;
  notice_served: boolean;
  notice_waived: boolean;
  notice_pay_in_lieu: boolean;
  show_cause_issued: boolean;
  show_cause_response: string | null;
  eligible_for_rehire: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface ExitChecklistItem {
  key: string;
  label: string;
  completed: boolean;
  completed_by: string | null;
  completed_at: string | null;
  notes: string | null;
}

export interface ExitChecklist {
  id: string;
  employee_id: string;
  separation_type: string;
  separation_id: string;
  items: ExitChecklistItem[];
  assigned_to: string | null;
  status: string;
  completed_at: string | null;
}

// Lightweight row returned by GET /separation/checklists (list view) — carries
// progress counts and the employee brief, but not the full item array.
export interface ChecklistListRow extends EmployeeBrief {
  id: string;
  employee_id: string;
  separation_type: string;
  separation_id: string;
  status: string;
  assigned_to: string | null;
  total_items: number;
  completed_items: number;
  completed_at: string | null;
  created_at: string | null;
}

export interface FullFinalSettlement extends EmployeeBrief {
  id: string;
  employee_id: string;
  separation_type: string;
  separation_id: string;
  pending_salary: number;
  leave_encashment: number;
  gratuity: number;
  bonus_due: number;
  notice_pay: number;
  other_earnings: number;
  loan_recovery: number;
  advance_recovery: number;
  notice_period_deduction: number;
  other_deductions: number;
  gross_earnings: number;
  total_deductions: number;
  net_payable: number;
  notes: string | null;
  status: string;
  calculated_by: string | null;
  approved_by: string | null;
  approved_at: string | null;
  paid_at: string | null;
  created_at: string | null;
}

export interface DashboardStats {
  resignations: {
    total: number;
    pending_approval: number;
    completed: number;
  };
  terminations: {
    total: number;
  };
  exit_checklists: {
    pending: number;
  };
  settlements: {
    pending: number;
  };
}

export interface ResignationCreate {
  resignation_date: string;
  requested_last_day: string;
  notice_period_days?: number;
  reason: string;
  additional_notes?: string;
  early_release_requested?: boolean;
  early_release_reason?: string;
}

export interface ResignationAction {
  action: 'APPROVE' | 'REJECT';
  remarks?: string;
  actual_last_day?: string;
}

export interface ResignationWithdraw {
  reason?: string;
}

export interface EarlyReleaseAction {
  approved: boolean;
  remarks?: string;
}

export interface TerminationCreate {
  employee_id: string;
  termination_date: string;
  last_working_day: string;
  termination_type: string;
  reason: string;
  additional_notes?: string;
  notice_served?: boolean;
  notice_waived?: boolean;
  notice_pay_in_lieu?: boolean;
  show_cause_issued?: boolean;
  show_cause_response?: string;
  eligible_for_rehire?: boolean;
}

export interface TerminationUpdate {
  reason?: string;
  additional_notes?: string;
  eligible_for_rehire?: boolean;
  show_cause_response?: string;
}

export interface ChecklistItemUpdate {
  key: string;
  completed: boolean;
  notes?: string;
}

export interface SettlementCreate {
  employee_id: string;
  separation_type: string;
  separation_id: string;
  pending_salary?: number;
  leave_encashment?: number;
  gratuity?: number;
  bonus_due?: number;
  notice_pay?: number;
  other_earnings?: number;
  loan_recovery?: number;
  advance_recovery?: number;
  notice_period_deduction?: number;
  other_deductions?: number;
  notes?: string;
}

export interface SettlementAction {
  action: 'APPROVE' | 'MARK_PAID' | 'RELEASE_RETAINED' | 'HOLD_RETAINED';
  notes?: string;
}

export interface RetainerConfig {
  id: string;
  enabled: boolean;
  amount: number;
  period_months: number;
  recurring: boolean;
  min_take_home_floor: number;
  status: string;
  start_year: number;
  start_month: number;
  next_amount_override: number | null;
}

export interface RetainerLedgerRow {
  year: number;
  month: number;
  kind: string;
  withheld: number;
  released: number;
  balance_after: number;
  cycle_index: number;
  settled: boolean;
}

export interface RetainerInfo {
  config: RetainerConfig | null;
  ledger: RetainerLedgerRow[];
  outstanding: number;
}

export type TabType = 'dashboard' | 'resignations' | 'terminations' | 'checklists' | 'settlements';